import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, XCircle, Clock, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { useDispatchLog } from '@/hooks/useUplog';
import { useQueryClient } from '@tanstack/react-query';
import type { DispatchLogEntry } from '@/api/uplog.api';
import { UplogDispatchDetailDialog } from './UplogDispatchDetailDialog';

const PAGE_SIZE = 20;

const MONTH_NAMES = [
  '',
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function StatusBadge({ status }: { status: string }) {
  if (status === 'sent' || status === 'delivered') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-green-300 text-green-700 dark:border-green-700 dark:text-green-400"
      >
        <CheckCircle2 className="h-3 w-3" />
        {status === 'delivered' ? 'Entregue' : 'Enviado'}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-red-300 text-red-700 dark:border-red-700 dark:text-red-400"
      >
        <XCircle className="h-3 w-3" />
        Falhou
      </Badge>
    );
  }
  if (status === 'test') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400"
      >
        <Clock className="h-3 w-3" />
        Teste
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1">
      <Clock className="h-3 w-3" />
      {status}
    </Badge>
  );
}

function LogRow({ entry, onClick }: { entry: DispatchLogEntry; onClick: () => void }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 border-b px-4 py-3 text-sm last:border-0 cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
    >
      <span className="font-mono text-xs w-36 shrink-0">{entry.documento}</span>
      <span className="text-muted-foreground w-16 shrink-0">
        {MONTH_NAMES[entry.mes]}/{entry.ano}
      </span>
      <span className="font-mono text-xs text-muted-foreground flex-1 min-w-0 truncate">
        → {entry.phone}
      </span>
      <StatusBadge status={entry.status} />
      <span className="text-xs text-muted-foreground shrink-0">
        {new Date(entry.dispatchedAt).toLocaleString('pt-BR')}
      </span>
      {entry.errorMessage && (
        <span className="w-full text-xs text-red-600 dark:text-red-400 pl-0">
          {entry.errorMessage}
        </span>
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  );
}

export function DispatchLogTable() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<DispatchLogEntry | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: result, isLoading } = useDispatchLog({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
  });

  const entries = result?.data ?? [];
  const meta = result?.meta;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Log de Disparos</CardTitle>
          <CardDescription>Histórico de disparos de boletos enviados pelo robô.</CardDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            void qc.invalidateQueries({ queryKey: ['uplog', 'dispatch-log'] });
          }}
          title="Atualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por documento, telefone ou status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        {isLoading && <TableSkeleton />}

        {!isLoading && entries.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {debouncedSearch
              ? `Nenhum resultado para "${debouncedSearch}".`
              : 'Nenhum disparo realizado ainda.'}
          </p>
        )}

        {entries.length > 0 && (
          <div className="rounded-md border">
            {entries.map((e) => (
              <LogRow key={e.id} entry={e} onClick={() => setSelected(e)} />
            ))}
          </div>
        )}

        {meta && meta.totalPages > 1 && (
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            pageSize={meta.pageSize}
            onPageChange={setPage}
          />
        )}
      </CardContent>

      <UplogDispatchDetailDialog
        entry={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </Card>
  );
}
